import React from 'react';
import './ProgressSlider.css';

interface ProgressSliderProps {
  value: number;
  onChange?: (value: number) => void;
  label?: string;
  ariaLabel?: string;
  disabled?: boolean;
  readOnly?: boolean;
  showPercentage?: boolean;
  showMarks?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function clampProgress(value: number): number {
  return Math.round(Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0)));
}

const ProgressSlider: React.FC<ProgressSliderProps> = ({
  value,
  onChange,
  label,
  ariaLabel,
  disabled = false,
  readOnly = false,
  showPercentage = true,
  showMarks = false,
  size = 'md',
  className = '',
}) => {
  const clampedValue = clampProgress(value);
  const isInteractive = Boolean(onChange) && !disabled && !readOnly;

  return (
    <div
      className={[
        'pms-progress-slider',
        `pms-progress-slider--${size}`,
        isInteractive ? 'pms-progress-slider--interactive' : 'pms-progress-slider--readonly',
        className,
      ].filter(Boolean).join(' ')}
      style={{ '--progress-value': `${clampedValue}%` } as React.CSSProperties}
    >
      {label && <span className="pms-progress-slider__label">{label}</span>}
      <div className="pms-progress-slider__control">
        <span className="pms-progress-slider__track-wrap">
          <span className="pms-progress-slider__track" aria-hidden="true">
            <span className="pms-progress-slider__fill" />
          </span>
          <span className="pms-progress-slider__thumb" aria-hidden="true" />
          <input
            className="pms-progress-slider__range"
            type="range"
            min={0}
            max={100}
            step={1}
            value={clampedValue}
            disabled={!isInteractive}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            onChange={(event) => {
              event.stopPropagation();
              onChange?.(clampProgress(Number(event.target.value)));
            }}
            aria-label={ariaLabel || label || `Progress ${clampedValue}%`}
            aria-valuetext={`${clampedValue}%`}
          />
        </span>
        {showPercentage && (
          <strong className="pms-progress-slider__value">{clampedValue}%</strong>
        )}
      </div>
      {showMarks && (
        <div className="pms-progress-slider__marks" aria-hidden="true">
          {[0, 25, 50, 75, 100].map((mark) => (
            <span key={mark}>{mark}%</span>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProgressSlider;
